import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Link, NavLink, Outlet, useLocation, useSearchParams } from 'react-router';
import { t } from '../i18n';
import { useAuth, useCan } from '../lib/auth';
import { useAgentStatus } from '../lib/queries';
import { fmtRelative, initials } from '../lib/format';
import type { AgentStatus } from '../lib/types';
import { Button } from './ui';
import { CommandPalette } from './CommandPalette';
import { DocumentDrawer } from './DocumentDrawer';
import { Assistant } from './Assistant';
import { useArchiveStatus } from '../pages/Archive';
import { IconBolt, IconBox, IconCart, IconCash, IconChecklist, IconFile, IconHome, IconInbox, IconLogout, IconMenu, IconMerge, IconReceipt, IconRepeat, IconSearch, IconSettings, IconTarget, IconTruck, IconUsers, IconTrend } from './icons';

export function agentTone(s: AgentStatus | undefined): { tone: 'ok' | 'warn' | 'bad' | 'off'; label: string } {
  if (!s) return { tone: 'off', label: t.common.loading };
  switch (s.overall) {
    case 'ok':
      return { tone: 'ok', label: t.agent.status.ok };
    case 'setup_required':
    case 'not_connected':
      return { tone: 'warn', label: t.agent.status[s.overall] };
    case 'error':
      return { tone: 'warn', label: t.agent.status.error };
    default:
      return { tone: 'bad', label: t.agent.status[s.overall] };
  }
}

export function Layout() {
  const { user, logout } = useAuth();
  const can = useCan();
  const pendingUsers = useQuery({
    queryKey: ['users', 'pending'],
    queryFn: () => api<{ count: number }>('/api/users/pending-count'),
    enabled: user?.role === 'admin',
    refetchInterval: 60_000,
  });
  const { data: agent } = useAgentStatus();
  const archive = useArchiveStatus(user?.role === 'admin');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const docId = Number(params.get('doc')) || null;

  useEffect(() => setNavOpen(false), [location.pathname]);

  // Ctrl/⌘+K opens global search; "/" too when not typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test((e.target as HTMLElement).tagName);
      if ((e.key === 'k' && (e.ctrlKey || e.metaKey)) || (e.key === '/' && !typing)) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Server reachability: polled so the user knows immediately when saving is not possible.
  const [browserOnline, setBrowserOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setBrowserOnline(true);
    const down = () => setBrowserOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => api<{ ok: boolean }>('/api/health'),
    refetchInterval: (q) => (q.state.status === 'error' ? 5000 : 20_000),
    retry: false,
  });
  const offline = !browserOnline || health.isError;

  const review = agent?.counters.needsReview ?? 0;
  const status = agentTone(agent);

  const closeDoc = () => {
    const next = new URLSearchParams(params);
    next.delete('doc');
    setParams(next, { replace: true });
  };

  return (
    <div className="shell">
      <aside className={`sidebar ${navOpen ? 'open' : ''}`} aria-label={t.nav.menu}>
        <Link to="/" className="brand" style={{ textDecoration: 'none', color: 'inherit' }}>
          <img className="brand-mark" src="/brand/mark.png" alt="" />
          <div>
            <div className="brand-name">{t.app.name}</div>
            <div className="brand-sub">{t.app.sub}</div>
          </div>
        </Link>
        <NavLink to="/" end className="nav-link">
          <IconHome /> {t.nav.dashboard}
        </NavLink>
        {can('sales.work') ? (
          <NavLink to="/today" className="nav-link">
            <IconChecklist /> סדר עבודה
          </NavLink>
        ) : null}
        <NavLink to="/customers" className="nav-link">
          <IconUsers /> {t.nav.customers}
        </NavLink>
        {can('finance.view') ? (
          <NavLink to="/invoices" className="nav-link">
            <IconReceipt /> {t.nav.invoices}
          </NavLink>
        ) : null}
        <NavLink to="/documents" className="nav-link">
          <IconFile /> {t.nav.documents}
        </NavLink>
        {can('finance.view') ? (
          <NavLink to="/finance" className="nav-link">
            <IconCash /> {t.nav.finance}
          </NavLink>
        ) : null}
        <div className="nav-label">מכירות</div>
        {can('sales.work') ? (
          <NavLink to="/followups" className="nav-link">
            <IconRepeat /> מעקב הזמנות
          </NavLink>
        ) : null}
        {can('orders.edit') ? (
          <NavLink to="/orders" className="nav-link">
            <IconCart /> הזמנות
          </NavLink>
        ) : null}
        {can('orders.pick') ? (
          <NavLink to="/pick" className="nav-link">
            <IconChecklist /> ליקוט
          </NavLink>
        ) : null}
        {can('catalog.edit') ? (
          <NavLink to="/products" className="nav-link">
            <IconBox /> קטלוג ומחירים
          </NavLink>
        ) : null}
        {can('inventory.edit') ? (
          <NavLink to="/inventory" className="nav-link">
            <IconBox /> מלאי
          </NavLink>
        ) : can('inventory.count') ? (
          <NavLink to="/inventory/counts" className="nav-link">
            <IconBox /> ספירת מלאי
          </NavLink>
        ) : null}
        {can('documents.edit') ? (
          <NavLink to="/billing" className="nav-link">
            <IconTruck /> חיוב סוף חודש
          </NavLink>
        ) : null}
        {can('profit.view') ? (
          <NavLink to="/profit" className="nav-link">
            <IconTrend /> רווחיות
          </NavLink>
        ) : null}
        {can('finance.view') ? (
          <NavLink to="/goals" className="nav-link">
            <IconTarget /> יעדים
          </NavLink>
        ) : null}
        {can('customers.edit') ? (
          <NavLink to="/duplicates" className="nav-link">
            <IconMerge /> לקוחות כפולים
          </NavLink>
        ) : null}
        <div className="nav-label">{t.nav.automation}</div>
        <NavLink to="/review" className="nav-link">
          <IconInbox /> {t.nav.review}
          {review > 0 ? <span className="nav-count">{review}</span> : null}
        </NavLink>
        {can('admin') && (archive.data?.counts.review ?? 0) > 0 ? (
          <NavLink to="/archive" className="nav-link">
            <IconInbox /> לקוחות MARKIT לבדיקה
            <span className="nav-count">{archive.data!.counts.review}</span>
          </NavLink>
        ) : null}
        {can('admin') ? (
          <NavLink to="/automation" className="nav-link">
            <IconBolt /> {t.nav.agent}
          </NavLink>
        ) : null}
        <NavLink to={pendingUsers.data?.count ? '/settings?tab=users' : '/settings'} className="nav-link">
          <IconSettings /> {t.nav.settings}
          {pendingUsers.data?.count ? <span className="nav-count" title="בקשות הרשמה ממתינות לאישור">{pendingUsers.data.count}</span> : null}
        </NavLink>
        <div className="sidebar-foot">
          <div className="user-chip">
            <span className="avatar">{initials(user?.name ?? '')}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="ellipsis strong small">{user?.name}</div>
              <div className="ellipsis faint small"><bdi>{user?.email}</bdi></div>
            </div>
            <Button variant="ghost" size="sm" icon onClick={logout} title={t.nav.logout} aria-label={t.nav.logout}>
              <IconLogout />
            </Button>
          </div>
        </div>
      </aside>
      {navOpen ? <div className="drawer-overlay mobile-only" style={{ zIndex: 44 }} onClick={() => setNavOpen(false)} /> : null}

      <div className="main">
        <header className="topbar">
          <Button variant="ghost" icon className="mobile-only" onClick={() => setNavOpen(true)} aria-label={t.nav.menu}>
            <IconMenu />
          </Button>
          <button
            className="input"
            style={{ maxWidth: 440, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--faint)', cursor: 'text', textAlign: 'start' }}
            onClick={() => setPaletteOpen(true)}
          >
            <IconSearch />
            <span className="ellipsis" style={{ flex: 1 }}>{t.nav.searchPlaceholder}</span>
            <span className="kbd desktop-only">Ctrl K</span>
          </button>
          <span className="spacer" />
          <Link to="/automation" className="agent-pill" title={agent?.agent.lastCheckedAt ? `${t.agent.lastChecked}: ${fmtRelative(agent.agent.lastCheckedAt)}` : undefined}>
            <span className={`status-line ${status.tone}`}>
              <span className="dot" />
            </span>
            <span className="desktop-only">{status.label}</span>
            {agent?.overall === 'ok' && agent.agent.lastCheckedAt ? <span className="faint desktop-only">· {fmtRelative(agent.agent.lastCheckedAt)}</span> : null}
          </Link>
        </header>
        {offline ? (
          <div className="offline-banner" role="alert">
            <strong>{browserOnline ? 'אין חיבור לשרת' : 'אין חיבור לאינטרנט'}</strong> — פעולות חדשות לא יישמרו עד שהחיבור יחזור. כל מה שכבר נשמר בטוח. המערכת תתחבר מחדש אוטומטית.
          </div>
        ) : null}
        <NewVersionBar />
        <main className="page">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <Assistant />
      {docId ? <DocumentDrawer id={docId} onClose={closeDoc} /> : null}
    </div>
  );
}

/** Adds ?doc=<id> to the current URL, which opens the document drawer on any page. */
export function useOpenDocument() {
  const [params, setParams] = useSearchParams();
  return (id: number) => {
    const next = new URLSearchParams(params);
    next.set('doc', String(id));
    setParams(next);
  };
}

/** The script this tab is running (index-<hash>.js) — a new deploy has a new hash. */
const runningScript = () => document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')?.getAttribute('src') ?? null;

/**
 * A tab opened before an update keeps running the old version. Every few minutes (and when the
 * tab comes back into view) the page checks whether a new version was deployed and offers a reload.
 */
function NewVersionBar() {
  const [stale, setStale] = useState(false);
  useEffect(() => {
    const mine = runningScript();
    if (!mine) return; // dev server
    const check = async () => {
      try {
        const html = await (await fetch('/', { cache: 'no-store' })).text();
        const live = html.match(/\/assets\/index-[^"']+\.js/)?.[0];
        if (live && live !== mine) setStale(true);
      } catch {
        /* offline — try later */
      }
    };
    const timer = window.setInterval(check, 5 * 60_000);
    const onVisible = () => document.visibilityState === 'visible' && void check();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  if (!stale) return null;
  return (
    <div className="offline-banner no-print" role="status" style={{ background: 'var(--accent-soft)', color: 'var(--ink)' }}>
      <strong>גרסה חדשה של המערכת זמינה</strong> — כדי לקבל את העדכונים האחרונים{' '}
      <Button size="sm" variant="primary" onClick={() => window.location.reload()}>רענון</Button>
    </div>
  );
}
